import { useEffect, useState } from 'react'
import {
  api,
  codeWords,
  shortDate,
  type ActivationGate,
  type Assessment,
  type ChannelConsent,
  type Clearance,
  type Contact,
  type Score,
  type Vendor,
  type VendorOracle,
} from '../../lib/api'
import { dpaCondition, dpaWord, waiverLine } from '../../lib/acceptance'
import { bandColour, bandRank, bandWord } from '../BandChip'
import { MiniTrack, Status, Toggletip, formatIN, type StageState, type Tone } from '../kit'
import { statusWord } from '../../lib/status'
import { QUESTIONNAIRE_STAGES } from '../assess/journey'
import { Chip } from '../StatusChip'
import { VendorDocumentsCard } from '../VendorDocumentsCard'
import {
  REVIEW_STATES,
  SCORED_STATES,
  WITH_VENDOR,
  dayOf,
  daysWord,
  findingsReading,
  isExitPlanFinding,
  questionnaireReading,
} from './facts'
import { ReasonTip } from './ReasonTip'
import { RecordDiffersNote, contractDates } from './ContractSource'
import { reasonWords } from '../../lib/bandLine'
import '../../styles/band-line.css'
import type { Place } from './sections'

// The vendor overview: the risk as a band, a score, a trend and the three
// things driving it, with Why? opening the full reading on the Risk tab; where
// the questionnaire is, as one line; the key facts, six rows at most, with All
// details opening the Profile; and, when the Oracle Fusion Cloud connector
// links the supplier, what Oracle has been told. Recent activity sits beside it
// (ActivityPanel.tsx). What to do next is the next step on the line above every
// tab (NextStep.tsx), so it is not repeated here (release 3.2, removal items 1
// and 2): no tab draws a second copy of a task, only its state and a link
// (release 3.3, the 7 Oct plan section 3.3).
//
// # WHAT MAY BE DRAWN
//
// Only what an API returned. The trend is drawn from the scores on this
// vendor's assessments and only when there are two or more, because one point
// is not a direction. A driver is a finding, a gate, a missing identifier or a
// late questionnaire that is on the record today. A fact the record does not
// hold says so ("Not on file", "Add dates") rather than leaving a gap that
// reads as nothing to see. Every driver and next step opens the section or
// screen where it is dealt with.

const CONTACT_ROLE: Record<string, string> = {
  PRIMARY: 'Primary',
  AUTHORISED_SIGNATORY: 'Authorised signatory',
  SECURITY: 'Security',
  PRIVACY: 'Privacy',
  GRIEVANCE: 'Grievance officer',
  ESCALATION: 'Escalation',
  BILLING: 'Billing',
}

const STAT_LABEL: Record<string, string> = {
  pan: 'PAN',
  gstin: 'GSTIN',
  cin: 'CIN',
  llpin: 'LLPIN',
  udyam_number: 'Udyam',
  trc_on_file: 'TRC',
  form_10f_on_file: 'Form 10F',
  no_pe_declaration: 'No-PE declaration',
}

type Scored = { assessment: Assessment; score: Score; at: string }

// How many of the newest assessments are read for a score. Each read is one
// request, and a trend of the last six assessments says as much as twenty.
const SCORE_READS = 6

function useScores(assessments: Assessment[]): Scored[] | null {
  const candidates = [...assessments]
    .filter((a) => SCORED_STATES.has(a.status) || REVIEW_STATES.has(a.status) || a.status === 'PROVISIONAL')
    .sort((a, b) => (b.submittedAt ?? b.issuedAt ?? '').localeCompare(a.submittedAt ?? a.issuedAt ?? ''))
    .slice(0, SCORE_READS)
  const key = candidates.map((a) => a.id).join(',')
  const [scores, setScores] = useState<Scored[] | null>(null)
  useEffect(() => {
    let live = true
    setScores(null)
    Promise.all(
      candidates.map((a) =>
        api
          .assessment(a.id)
          .then((d) => (d.score ? { assessment: d.assessment, score: d.score, at: d.score.computedAt ?? d.assessment.submittedAt ?? '' } : null))
          .catch(() => null),
      ),
    ).then((found) => {
      if (!live) return
      // Oldest first, so the line reads left to right as time.
      setScores(found.filter((s): s is Scored => s !== null && s.at !== '').sort((x, y) => x.at.localeCompare(y.at)))
    })
    return () => {
      live = false
    }
    // The candidate list is rebuilt every render; its ids are the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return scores
}

function Sparkline({ points }: { points: Scored[] }) {
  const ranked = points.map((p) => ({ p, rank: bandRank(p.score.finalBand) })).filter((x): x is { p: Scored; rank: number } => x.rank !== null)
  if (ranked.length < 2) return null
  const W = 300
  const H = 56
  const pad = 6
  const x = (i: number) => pad + (i * (W - pad * 2)) / (ranked.length - 1)
  const y = (rank: number) => H - pad - ((rank - 1) * (H - pad * 2)) / 3
  const line = ranked.map((r, i) => (i === 0 ? 'M' : 'L') + x(i).toFixed(1) + ' ' + y(r.rank).toFixed(1)).join(' ')
  const last = ranked[ranked.length - 1] as { p: Scored; rank: number }
  const colour = bandColour(last.p.score.finalBand)
  const said = ranked.map((r) => bandWord(r.p.score.finalBand) + ' on ' + dayOf(r.p.at)).join(', ')
  return (
    <svg className="vd-spark" viewBox={'0 0 ' + W + ' ' + H} preserveAspectRatio="none" role="img" aria-label={'Band over ' + ranked.length + ' scores: ' + said}>
      {[1, 2, 3, 4].map((r) => (
        <line key={r} x1={0} x2={W} y1={y(r)} y2={y(r)} className="vd-spark-grid" />
      ))}
      <path d={line} fill="none" stroke={colour} strokeWidth={2} vectorEffect="non-scaling-stroke" />
      <circle cx={x(ranked.length - 1)} cy={y(last.rank)} r={4} fill={colour} />
    </svg>
  )
}

type Driver = { key: string; text: string; colour: string; go?: Place; href?: string }

// A late questionnaire is not a driver of the band: it is the questionnaire's
// state, read on the Questionnaire panel and acted on in the Next step bar.
function drivers(vendor: Vendor, latest: Score | null): Driver[] {
  const out: Driver[] = []
  const f = findingsReading(vendor)
  const items = vendor.findings?.items ?? []
  const critical = f.bySeverity.CRITICAL ?? 0
  const high = f.bySeverity.HIGH ?? 0
  if (critical > 0) {
    out.push({
      key: 'critical',
      text: f.complete ? critical + (critical === 1 ? ' critical finding' : ' critical findings') : 'Critical findings open',
      colour: 'var(--crit-solid)',
      go: { section: 'risk', part: 'findings' },
    })
  }
  const exitPlan = items.find(isExitPlanFinding)
  if (exitPlan) out.push({ key: 'exit-plan', text: exitPlan.title, colour: 'var(--band-high)', go: { section: 'exit', part: null } })
  if (latest?.needsException.length) {
    out.push({ key: 'acceptance', text: 'Written risk acceptance needed', colour: 'var(--spec-solid)', go: { section: 'risk', part: null } })
  }
  const gaps = vendor.statutory?.gaps ?? []
  if (gaps.length > 0) {
    const names = gaps.map((g) => STAT_LABEL[g.field] ?? g.field)
    const text = names.length <= 2 ? names.join(' and ') + ' missing' : names.length + ' identifiers missing'
    out.push({ key: 'statutory', text, colour: 'var(--warn-solid)', go: { section: 'profile', part: 'statutory' } })
  }
  if (high > 0) {
    out.push({
      key: 'high',
      text: f.complete ? high + (high === 1 ? ' high finding' : ' high findings') : 'High findings open',
      colour: 'var(--band-high)',
      go: { section: 'risk', part: 'findings' },
    })
  }
  if (vendor.currentBand === 'INSUFFICIENT_EVIDENCE') {
    out.push({ key: 'evidence', text: 'Too few answers for a band', colour: 'var(--neu-solid)', go: { section: 'risk', part: null } })
  }
  return out.slice(0, 3)
}

// How each active contact may be written to. The same reading the contacts
// panel gives channel by channel: an address on file, not objected to, and for
// WhatsApp the contact's recorded agreement.
function reachableBy(contacts: Contact[], consents: ChannelConsent[]): string[] {
  const out = new Set<string>()
  for (const c of contacts) {
    if (c.erasedAt || !c.isActive) continue
    const consent = (ch: string) => consents.find((k) => k.contactId === c.id && k.channel === ch)
    if (c.email && !consent('EMAIL')?.objectedAt) out.add('Email')
    if (c.mobileE164 && !consent('SMS')?.objectedAt) out.add('SMS')
    const wa = consent('WHATSAPP')
    if (c.mobileE164 && wa?.optedInAt && !wa.objectedAt) out.add('WhatsApp')
  }
  return ['Email', 'SMS', 'WhatsApp'].filter((ch) => out.has(ch))
}

// The one reading of the questionnaire on the vendor page (release 3.2,
// removal item 2): its state in the vocabulary's words, where it is on its
// track as four dots, and the way into it, on one line (release 3.3, the 7 Oct
// plan section 3.3). Chasing it is the next step's job, so the line has no
// button of its own for that.
export function questionnaireDots(a: Assessment, late: boolean): { states: StageState[]; at: number } {
  // Which of the four stages the questionnaire is at. A late one is blocked at
  // Vendor answers, because that is where it is waiting; the stages before it
  // are done and the ones after it have not started.
  const scored = SCORED_STATES.has(a.status)
  const at = scored ? 4 : REVIEW_STATES.has(a.status) || a.status === 'PROVISIONAL' ? 2 : 1
  const states: StageState[] = QUESTIONNAIRE_STAGES.map((_, i) =>
    i === 0 || i < at ? 'done' : i === at ? (late && i === 1 ? 'blocked' : 'current') : 'upcoming',
  )
  return { states, at }
}

export function QuestionnaireLine({ assessments }: { assessments: Assessment[] }) {
  const reading = questionnaireReading(assessments)
  const a = reading.assessment
  const chip = { word: reading.word, tone: reading.tone, icon: reading.icon }
  if (!a) {
    return (
      <section className="vd-qline" aria-label="Questionnaire">
        <span className="vd-qline-h">Questionnaire</span>
        <Chip s={chip} />
        <span className="vd-quiet">None sent to this vendor yet</span>
      </section>
    )
  }
  const late = reading.lateBy !== null
  const { states, at } = questionnaireDots(a, late)
  const stage = QUESTIONNAIRE_STAGES[Math.min(at, QUESTIONNAIRE_STAGES.length - 1)] ?? ''
  return (
    <section className="vd-qline" aria-label="Questionnaire">
      <span className="vd-qline-h">Questionnaire</span>
      <Chip s={chip} />
      <MiniTrack states={states} label={'Questionnaire ' + a.reference + ': stage ' + Math.min(at + 1, 4) + ' of 4, ' + stage.toLowerCase()} />
      <span className="kit-mono">{a.reference}</span>
      {a.dueOn && WITH_VENDOR.has(a.status) && <span className={late ? 'vd-late' : 'vd-quiet'}>{late ? daysWord(reading.lateBy ?? 0) + ' late' : 'Due ' + shortDate(a.dueOn)}</span>}
      <span className="vd-grow" />
      <a className="linkish" href={'#/assessment/' + a.id}>
        Open questionnaire
      </a>
    </section>
  )
}

const WRITE_WORD: Record<string, { word: string; tone: Tone }> = {
  SENT: { word: 'Sent', tone: 'ok' },
  PENDING: { word: 'Queued', tone: 'neutral' },
  SENDING: { word: 'Sending', tone: 'info' },
  FAILED: { word: 'Failed', tone: 'crit' },
  GAVE_UP: { word: 'Gave up', tone: 'crit' },
  SUPERSEDED: { word: 'Superseded', tone: 'plain' },
}

function money(amount: number, currency: string): string {
  const whole = formatIN(Math.round(amount))
  return currency === 'INR' ? '\u20b9' + whole : whole + ' ' + currency
}

// What Oracle Fusion Cloud holds for this supplier, as the record read carries
// it. The site counts mean something only once the sites have been read
// (sitesObservedAt): before that they are zero because nobody has looked, so
// the block says "Not read yet" instead of "No sites held". A spend period with
// no currency had no invoices, which is an answer, not a missing figure.
function OracleBlock({ oracle }: { oracle: VendorOracle }) {
  const read = Boolean(oracle.sitesObservedAt)
  const write = oracle.lastWrite
  const writeWord = write ? (WRITE_WORD[write.status] ?? { word: codeWords(write.status), tone: 'neutral' as Tone }) : null
  const spend = oracle.spend12m
  return (
    <section className="vd-panel" aria-labelledby="vd-oracle-h">
      <div className="vd-panel-head">
        <span className="vd-mark" aria-hidden="true">
          OF
        </span>
        <h2 id="vd-oracle-h">Oracle Fusion Cloud</h2>
        {!read ? (
          <Status tone="neutral">Sites not read</Status>
        ) : oracle.sitesHeld > 0 ? (
          <Status tone="warn">{oracle.sitesHeld === 1 ? '1 site held' : oracle.sitesHeld + ' sites held'}</Status>
        ) : (
          <Status tone="ok">No sites held</Status>
        )}
      </div>
      <dl className="vd-kv">
        <div>
          <dt>Supplier number</dt>
          <dd className="kit-mono">{oracle.supplierNumber}</dd>
        </div>
        <div>
          <dt>Sites held by us</dt>
          <dd>
            {read ? (
              <span className="vd-inline">
                {oracle.sitesHeld}
                <span className="vd-quiet">as of {dayOf(oracle.sitesObservedAt)}</span>
              </span>
            ) : (
              <span className="vd-quiet">Not read yet</span>
            )}
          </dd>
        </div>
        {read && oracle.sitesHeldByOthers > 0 && (
          <div>
            <dt>Held by someone else</dt>
            <dd className="vd-inline">
              {oracle.sitesHeldByOthers}
              <Toggletip label="About holds placed by others">A hold somebody else placed in Oracle is never lifted from here. Only holds this product placed are released.</Toggletip>
            </dd>
          </div>
        )}
        {spend && (
          <div>
            <dt>Spend, last 12 months</dt>
            <dd>
              {spend.currency ? (
                <span className="vd-inline">
                  {money(spend.amount, spend.currency)}
                  <span className="vd-quiet">{spend.invoices === 1 ? '1 invoice' : spend.invoices + ' invoices'}</span>
                </span>
              ) : (
                <span className="vd-quiet">No invoices</span>
              )}
            </dd>
          </div>
        )}
        {write && writeWord && (
          <div className="vd-kv-wide">
            <dt>Last write</dt>
            <dd>
              <span className="vd-inline">
                <Status tone={writeWord.tone}>{writeWord.word}</Status>
                <span className="kit-mono">{dayOf(write.at)}</span>
                <ReasonTip label="What Oracle was asked for" text={write.sentence} />
              </span>
            </dd>
          </div>
        )}
      </dl>
    </section>
  )
}

const DPA_TONE: Record<string, Tone> = {
  met: 'ok',
  outstanding: 'warn',
  'not required': 'plain',
}

// The contract, as a key fact. A standing contract waiver is said in the
// founder's words until the contract is filed (release 3.2: a waiver shows on
// the vendor page whatever the vendor's status), across the whole row because
// the reason is a sentence, and in place of the dates: a waiver stands only
// while no contract is on file, so the dates on the record describe no filed
// contract.
export function ContractFact({ vendor, gate, onGo }: { vendor: Vendor; gate: ActivationGate | null; onGo: (p: Place) => void }) {
  const waiver = gate?.contractWaiver
  // The filed contract's dates where one is filed (release 3.3, C13).
  const { start, end, source } = contractDates(vendor)
  const dates = start || end
  return (
    <div className={waiver ? 'vd-kv-wide' : undefined}>
      <dt>Contract</dt>
      <dd>
        {waiver ? (
          <span data-testid="overview-contract-waiver">{waiverLine(waiver)}</span>
        ) : dates ? (
          <>
            <span className="kit-mono">
              {(start ? shortDate(start) : 'No start') + ' to ' + (end ? shortDate(end) : 'no end')}
            </span>
            {source && source !== 'RECORD' && <span className="cell-sub">From the filed {source}</span>}
            <RecordDiffersNote vendor={vendor} />
          </>
        ) : (
          <button type="button" className="linkish vd-fact-link" onClick={() => onGo({ section: 'contracts', part: null })}>
            Add dates
          </button>
        )}
      </dd>
    </div>
  )
}

// The data processing agreement, as the activation gate read it today: its
// state in a word and what was found, which is the gate's own sentence (it
// says whether the vendor processes personal data and whether an agreement is
// on file). Absent where the gate did not carry it.
export function DpaFact({ gate }: { gate: ActivationGate | null }) {
  const dpa = dpaCondition(gate)
  if (!dpa) return null
  return (
    <div className="vd-kv-wide">
      <dt>Data processing agreement</dt>
      <dd>
        <span className="vd-inline">
          <Status tone={DPA_TONE[dpa.state] ?? 'neutral'}>{dpaWord(dpa.state)}</Status>
        </span>
        {dpa.detail && (
          <span className="vd-quiet" style={{ display: 'block', marginTop: 4 }} data-testid="overview-dpa-detail">
            {dpa.detail}
          </span>
        )}
      </dd>
    </div>
  )
}

export function Overview({ vendor, assessments, onGo }: { vendor: Vendor; assessments: Assessment[]; onGo: (p: Place) => void }) {
  const scores = useScores(assessments)
  const [contacts, setContacts] = useState<Contact[] | null>(null)
  const [consents, setConsents] = useState<ChannelConsent[]>([])
  const [clearance, setClearance] = useState<Clearance | null>(null)
  // The activation gate's reading, for the contract waiver and the data
  // processing agreement in the key facts. A tenant with no approval matrix,
  // or a read that fails, simply leaves both as they were.
  const [gate, setGate] = useState<ActivationGate | null>(null)

  useEffect(() => {
    let live = true
    setGate(null)
    api
      .vendorActivation(vendor.id)
      .then((v) => {
        if (live) setGate(v.activation)
      })
      .catch(() => undefined)
    api
      .contacts(vendor.id)
      .then((r) => {
        if (live) setContacts(r.contacts ?? [])
      })
      .catch(() => {
        if (live) setContacts([])
      })
    api
      .vendorChannels(vendor.id)
      .then((r) => {
        if (live) setConsents(r.consents ?? [])
      })
      .catch(() => undefined)
    // A tenant that has not set up purchase requests, or a role the read
    // refuses, simply has no clearance row on the overview.
    api
      .vendorClearance(vendor.id)
      .then((c) => {
        if (live) setClearance(c)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [vendor.id])

  const latest = scores && scores.length > 0 ? (scores[scores.length - 1] as Scored) : null
  const previous = scores && scores.length > 1 ? (scores[scores.length - 2] as Scored) : null
  const band = vendor.currentBand
  const moved =
    latest && previous && bandRank(latest.score.finalBand) !== null && bandRank(previous.score.finalBand) !== null && latest.score.finalBand !== previous.score.finalBand
      ? ((bandRank(latest.score.finalBand) ?? 0) > (bandRank(previous.score.finalBand) ?? 0) ? 'Up from ' : 'Down from ') +
        bandWord(previous.score.finalBand) +
        ' on ' +
        dayOf(latest.at)
      : ''
  const why = drivers(vendor, latest?.score ?? null)
  const invited = contacts?.find((c) => c.wouldBeInvited) ?? contacts?.find((c) => c.isActive && !c.erasedAt && c.email) ?? null
  const channels = contacts ? reachableBy(contacts, consents) : []
  const clear = clearance ? statusWord('clearance', clearance.status) : null
  const clearTip = clearance?.reasons[0]?.words

  return (
    <div className="vd-overview">
      <section className="vd-panel" aria-labelledby="vd-risk-h">
        <div className="vd-panel-head">
          <h2 id="vd-risk-h">Risk</h2>
          {latest && <span className="vd-quiet">Scored {dayOf(latest.at)}</span>}
          {/* The full reading, "Why this band", is the Risk tab's first part;
              the card keeps the band, the score and what drives it. */}
          <button type="button" className="linkish" onClick={() => onGo({ section: 'risk', part: null })}>
            Why?
          </button>
        </div>
        <div className={'vd-score' + (scores && scores.length > 1 ? ' vd-score-trend' : '')}>
          <div className="vd-bandbig">
            <span className="vd-bandword" style={{ color: bandColour(band) }}>
              {bandWord(band)}
            </span>
            <span className="vd-quiet">{[latest ? 'Control score ' + Math.round(latest.score.controlScore) + '/100' : band ? '' : 'No score on file', moved].filter(Boolean).join(', ')}</span>
          </div>
          {scores && scores.length > 1 && <Sparkline points={scores} />}
        </div>
        {/* The band's whole line, which the head above cuts at one line
            (release 3.3, N2; review of 12 Oct, S5). */}
        {reasonWords(vendor.bandReason) && <p className="band-why-lead">{reasonWords(vendor.bandReason)}</p>}
        {why.length > 0 ? (
          <ul className="vd-drivers" aria-label="What drives this band">
            {why.map((d) => (
              <li key={d.key}>
                {d.href ? (
                  <a className="vd-driver" href={d.href}>
                    <i style={{ background: d.colour }} aria-hidden="true" />
                    {d.text}
                  </a>
                ) : (
                  <button type="button" className="vd-driver" onClick={() => d.go && onGo(d.go)}>
                    <i style={{ background: d.colour }} aria-hidden="true" />
                    {d.text}
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          band && <p className="vd-quiet vd-drivers-none">Nothing on the record is pushing this band up</p>
        )}
      </section>

      <QuestionnaireLine assessments={assessments} />

      <section className="vd-panel" aria-labelledby="vd-facts-h">
        <div className="vd-panel-head">
          <h2 id="vd-facts-h">Key facts</h2>
          <button type="button" className="linkish" onClick={() => onGo({ section: 'profile', part: null })}>
            All details
          </button>
        </div>
        <dl className="vd-kv">
          <div>
            <dt>Legal name</dt>
            <dd>{vendor.legalName}</dd>
          </div>
          <div>
            <dt>PAN</dt>
            <dd className={vendor.pan ? 'kit-mono' : 'vd-quiet'}>{vendor.pan || 'Not on file'}</dd>
          </div>
          <div>
            <dt>Contact</dt>
            <dd>
              {contacts === null ? (
                <span className="vd-quiet">Reading</span>
              ) : invited ? (
                <button type="button" className="linkish vd-fact-link" onClick={() => onGo({ section: 'profile', part: 'contacts' })}>
                  {(invited.fullName || invited.email || 'Unnamed') + ', ' + (CONTACT_ROLE[invited.role] ?? codeWords(invited.role))}
                </button>
              ) : (
                <button type="button" className="linkish vd-fact-link" onClick={() => onGo({ section: 'profile', part: 'contacts' })}>
                  Add a contact
                </button>
              )}
              {/* How the contacts can be written to, under the contact rather
                  than a row of its own: the key facts are six rows at most. */}
              {contacts !== null && <span className="vd-quiet vd-fact-sub">{channels.length ? 'Reachable by ' + channels.join(', ') : 'Nobody reachable'}</span>}
            </dd>
          </div>
          <ContractFact vendor={vendor} gate={gate} onGo={onGo} />
          {clear && (
            <div>
              <dt>Purchase clearance</dt>
              <dd className="vd-inline">
                <Chip s={clear} />
                {clearTip && clearTip.length <= 200 && <Toggletip label="Why this clearance">{clearTip}</Toggletip>}
              </dd>
            </div>
          )}
          <DpaFact gate={gate} />
        </dl>
      </section>

      {vendor.oracle && <OracleBlock oracle={vendor.oracle} />}

      {/* The documents card alone, across the column. Website checks moved to
          the Risk tab (T11 of the review of 21 September, decision 3 of the
          wave 3 design), where the band they feed is read. */}
      <div className="vd-cards">
        <VendorDocumentsCard vendorId={vendor.id} onAdd={() => onGo({ section: 'contracts', part: 'contracts' })} />
      </div>
    </div>
  )
}

